/**
 * Central Column Configuration Registry
 *
 * Single source of truth for all table column configurations across the platform.
 *
 * Organization:
 * - shared: Columns used by multiple entity types (email, tags, status, dates)
 * - seed: Seed-specific columns (registrar, registrant, country)
 * - asset: Asset-specific columns (attackSurface, origins, port, protocol)
 * - technology: Technology-specific columns (vendor, name)
 * - risk: Risk-specific columns (future)
 *
 * Usage:
 * ```typescript
 * import { getColumnWidth } from '@/config/columns/columnRegistry';
 *
 * const emailWidth = getColumnWidth('seed', 'email');
 * // { fixedWidth: 200, minWidth: 200, maxWidth: 200 }
 * ```
 *
 * File History:
 * - 2025-10-19: Initial creation - Day 1 of centralized column configuration migration
 *               Extracted all widths from Seeds, Assets, and Technologies table configs
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Column width configuration
 *
 * Width properties:
 * - fixedWidth: The actual rendered width in pixels (for fixed-width columns only)
 * - minWidth: Minimum width constraint (required for all columns)
 * - maxWidth: Maximum width constraint (omit for truly expandable columns)
 *
 * Column types:
 * - Fixed: { fixedWidth: 200, minWidth: 200, maxWidth: 200 }
 * - Expandable: { minWidth: 200 } (no fixedWidth or maxWidth)
 * - Flexible: { minWidth: 200, maxWidth: 400 } (no fixedWidth)
 */
export interface ColumnWidth {
  fixedWidth?: number;
  minWidth: number;
  maxWidth?: number;
}

/**
 * Table variant type
 *
 * Variants allow different column widths based on context:
 * - default: Full-page tables with ample horizontal space
 * - compact: Drawer/constrained contexts (~900px or less)
 */
export type TableVariant = 'default' | 'compact';

/**
 * Variant-aware column width configuration
 *
 * Allows specifying different widths for different table contexts.
 * The compact variant uses partial overrides - only specify properties
 * that differ from the default.
 *
 * @example
 * ```typescript
 * const variantWidth: VariantColumnWidth = {
 *   default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
 *   compact: { fixedWidth: 140 }, // Only override fixedWidth
 * };
 * ```
 */
export interface VariantColumnWidth {
  default: ColumnWidth;
  compact?: Partial<ColumnWidth>;
}

/**
 * Type guard to check if width configuration has variant support
 */
function hasVariants(
  width: ColumnWidth | VariantColumnWidth
): width is VariantColumnWidth {
  return 'default' in width;
}

/**
 * Column configuration entry
 *
 * Contains all metadata for a single column including:
 * - Width configuration (supports variant-aware widths)
 * - Display label
 * - Behavior flags (sortable, filterable)
 * - Additional metadata
 */
export interface ColumnConfig {
  width: ColumnWidth | VariantColumnWidth;
  label?: string;
  sortable?: boolean;
  filterable?: boolean;
  info?: string;
}

/**
 * Entity-specific column configurations
 * Maps column keys to their configurations
 */
export interface EntityColumns {
  [columnKey: string]: ColumnConfig;
}

/**
 * Entity type literal union
 */
export type EntityType =
  | 'shared'
  | 'seed'
  | 'asset'
  | 'technology'
  | 'risk'
  | 'agent'
  | 'agentUser'
  | 'agentCredential'
  | 'customer'
  | 'job'
  | 'account'
  | 'access'
  | 'capability';

/**
 * Central column registry organized by entity type
 */
export interface ColumnRegistry {
  shared: EntityColumns;
  seed: EntityColumns;
  asset: EntityColumns;
  technology: EntityColumns;
  risk: EntityColumns;
  agent: EntityColumns;
  agentUser: EntityColumns;
  agentCredential: EntityColumns;
  customer: EntityColumns;
  job: EntityColumns;
  account: EntityColumns;
  access: EntityColumns;
  capability: EntityColumns;
}

// ============================================================================
// CENTRAL COLUMN REGISTRY
// ============================================================================

/**
 * Column Registry
 *
 * ALL column width configurations for ALL entity types.
 * Edit this file to change column widths across the platform.
 *
 * Width Strategy:
 * - Fixed columns: fixedWidth = minWidth = maxWidth (all equal)
 * - Expandable columns: minWidth ONLY (no fixedWidth, no maxWidth - allows flexGrow)
 * - Flexible columns: minWidth + maxWidth (no fixedWidth - constrained expansion)
 *
 * Source Files (Day 1 migration):
 * - Seeds: modules/chariot/ui/src/sections/seeds/config/columns.ts
 * - Assets: modules/chariot/ui/src/sections/asset/config/columns.ts
 * - Technologies: modules/chariot/ui/src/sections/technology/config/columns.ts
 */
export const COLUMN_REGISTRY: ColumnRegistry = {
  /**
   * ========================================================================
   * SHARED COLUMNS
   * ========================================================================
   *
   * Columns used by multiple entity types with consistent behavior.
   *
   * Email column is used by:
   * - Seeds (domain seed email field)
   * - Risks (future)
   *
   * Status column is used by:
   * - Seeds (Active, Frozen status)
   * - Assets (Active, Frozen status)
   *
   * Date columns (created, visited) are used by:
   * - Seeds (First Seen, Last Seen)
   * - Assets (First Seen, Last Seen)
   * - Technologies (First Seen, Last Seen)
   *
   * Tags column is used by:
   * - Assets (user-defined tags)
   * - Technologies (user-defined tags)
   * - Seeds (future)
   */
  shared: {
    // Email column (Seeds, Risks)
    // Updated: 2026-06-24 - 200px→280px. The seed "Registrant Email" column clipped
    //   typical registrant addresses at 200px (e.g. "domain.billing@zurich.com" sheared
    //   to "…@zurich.co"). 280px fits common registrant emails; the seed `identifier`
    //   absorber column takes up the slack under the non-responsive layout.
    // Source: Seeds SEED_COLUMN_WIDTHS_DOMAIN.email
    email: {
      width: { fixedWidth: 280, minWidth: 280, maxWidth: 280 },
      label: 'Email',
      sortable: true,
      filterable: true,
      info: 'Email address',
    },

    // Status column (Seeds, Assets)
    // Source: Seeds SEED_COLUMN_WIDTHS_DOMAIN.status, Assets ASSET_COLUMN_WIDTHS.status
    // Updated: 2026-06-13 - Increased 70px→130px so the icon+label StatusChip fits
    //   without clipping (widest pill is asset "UNAVAILABLE"). The pill is
    //   content-sized (w-fit) inside an overflow:hidden cell, so a too-narrow fixed
    //   width shears the pill. Matches the risk.status 120/130 band.
    status: {
      width: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      label: 'Status',
      sortable: true,
      filterable: true,
      info: 'Entity status (Active, Frozen, etc.)',
    },

    // Created date - First Seen (Assets, Seeds, Technologies)
    // Updated: 2026-06-13 - 90px→120px. At 90px the relative-time strings clipped
    // (cell padding leaves ~87px; "11 hours ago" needs ~95px). 120px fits the
    // common values ("1 day ago", "26 days ago", "Apr 30, 2025").
    created: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'First Seen',
      sortable: true,
      info: 'Date entity was first discovered',
    },

    // Updated/Visited date - Last Seen (Assets, Technologies)
    // Updated: 2026-06-13 - 90px→120px. At 90px "11 hours ago" clipped (verified on
    // the assets table). 120px fits the common relative-time values.
    visited: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Last Seen',
      sortable: true,
      info: 'Date entity was last observed',
    },

    // Last Tested - lastAgentScan (Assets). The RFC3339 timestamp an attack
    // agent (persistent hunt) last tested this asset, stamped by the hunt's
    // spawn_agent dispatch. Mirrors the visited ("Last Seen") date column.
    lastAgentScan: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Last Tested',
      sortable: true,
      info: 'Date this asset was last tested by an attack agent',
    },

    // Tags column (Assets, Technologies)
    // Source: Assets ASSET_COLUMN_WIDTHS.tags, Technologies TECHNOLOGY_COLUMN_WIDTHS.tags
    // Note: Assets uses 80px base, Technologies uses 150px base - storing Assets width as default
    // Updated: 2025-10-27 - Increased from 40px to 100px for tag chips
    // Updated: 2026-06-13 - Increased from 100px to 130px for wider tag chips / more tags
    tags: {
      width: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      label: 'Tags',
      filterable: true,
      info: 'User-defined tags',
    },

    /**
     * ========================================================================
     * NETWORK COLUMNS (SHARED ACROSS DRAWERS)
     * ========================================================================
     *
     * Source: Drawer table migrations
     * Used by: Asset Drawer (Technologies tab), Technology Drawer (Assets Impacted tab)
     *
     * Migration: 2025-11-18
     * - Extracted from inline definitions in drawer tables
     * - Standardized widths based on data analysis
     */

    // Port column - Network port number
    // Source: TechnologiesTab.tsx (100px), TechnologyAssetsImpacted.tsx (100px)
    port: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Port',
      sortable: true,
      filterable: true,
      info: 'Network port number (1-65535)',
    },

    // Protocol column - Network protocol
    // Source: TechnologiesTab.tsx (120px)
    protocol: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Protocol',
      sortable: true,
      filterable: true,
      info: 'Network protocol (TCP, UDP, HTTP, HTTPS)',
    },

    // Service column - Network service name
    // Source: TechnologiesTab.tsx (120px), TechnologyAssetsImpacted.tsx (120px)
    service: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Service',
      sortable: true,
      filterable: true,
      info: 'Network service name',
    },

    // IP Address column - IPv4 or IPv6 address
    // Source: TechnologyAssetsImpacted.tsx (200px)
    ipAddress: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'IP Address',
      sortable: true,
      filterable: true,
      info: 'IPv4 or IPv6 address',
    },

    // Domain Name column - FQDN
    // Source: TechnologyAssetsImpacted.tsx (flexible)
    // Updated: 2025-11-18 - Added maxWidth: 400 to prevent horizontal scroll in drawers
    domainName: {
      width: { minWidth: 200, maxWidth: 400 }, // Bounded flexibility
      label: 'Domain Name',
      sortable: true,
      filterable: true,
      info: 'Fully qualified domain name (FQDN)',
    },
  },

  /**
   * ========================================================================
   * SEED-SPECIFIC COLUMNS
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/seeds/config/columns.ts
   *
   * Three seed types with different column configurations:
   * - Domain seeds: identifier, registrar, email, registrant, origin
   * - IP seeds: identifier, registrant, registrar, country, origin
   * - WebApp seeds: identifier, origin
   *
   * Common columns (status, email) are in shared section.
   */
  seed: {
    // Identifier column - Used by all seed types
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.identifier (200px), SEED_COLUMN_WIDTHS_WEBAPP.identifier (300px)
    // The seed table's flexible absorber column: minWidth only (no fixedWidth, no
    // maxWidth) so it routes through the isExpandable/minWidth path in
    // applyColumnWidth. Under tableLayout:'fixed' in non-responsive mode its <col>
    // becomes width:auto and soaks up leftover viewport width, letting the
    // fixed-width seed columns render at their true spec instead of being
    // proportionally inflated. Mirrors the asset `identifier` ({ minWidth: 200 }).
    identifier: {
      width: { minWidth: 220 },
      label: 'Identifier',
      sortable: true,
      filterable: true,
      info: 'Seed identifier (domain, IP, URL)',
    },

    // Registrar column - Domain and IP seeds
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.registrar
    // Updated: 2026-06-16 - 100px→150px. At 100px registrar names ("Squarespace")
    //   and the sort chevron clipped under the non-responsive CanonicalTable.
    // Updated: 2026-06-16 - 150px→240px. The IP-tab label "Internet Service
    //   Provider" (~200px) needs ~236px for header + sort chevron, and long domain
    //   registrar names ("eRank Ltd t/a eRankTrust Ltd", "Network Solutions, LLC")
    //   truncated too early at 150px; 240px fits the header and gives values more room.
    registrar: {
      width: { fixedWidth: 240, minWidth: 240, maxWidth: 240 },
      label: 'Registrar',
      sortable: true,
      filterable: true,
      info: 'Domain registrar',
    },

    // Registrant column - Domain and IP seeds
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.registrant
    // Updated: 2026-06-16 - 120px→175px. At 120px the "Registrant Organization"
    //   header (IP tab) and org names clipped.
    // Updated: 2026-06-16 - 175px→230px. The "Registrant Organization" header
    //   ≈ 190px + 12px th padding (pl-2+pr-1) + 24px hover sort chevron (gap-2 8px +
    //   size-4 16px) = ~226px; 175px truncated it ("Registrant Organizatio…").
    //   230px fits the full header + sort chevron.
    // Updated: 2026-06-24 - 230px→300px. Widened so "Registrant Organization" values
    //   (long org names) get more room beyond just fitting the header.
    registrant: {
      width: { fixedWidth: 300, minWidth: 300, maxWidth: 300 },
      label: 'Registrant',
      sortable: true,
      filterable: true,
      info: 'Domain registrant',
    },

    // Customer column - IP seeds only
    // The ARIN whois reassignment customer (the actual owner of a reassigned
    // range), distinct from the "Registrant Organization" (upstream ISP). Mirrors
    // the registrant width (300px) since it holds the same class of org-name values
    // and shares the sibling column header footprint on the IP/CIDR tab.
    customer: {
      width: { fixedWidth: 300, minWidth: 300, maxWidth: 300 },
      label: 'Customer',
      sortable: true,
      filterable: true,
      info: 'Customer the IP range is reassigned to (RIR whois)',
    },

    // Origin column - All seed types
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.origin
    // Updated: 2025-10-27 - Increased from 50px to 100px for origin badges
    // Updated: 2026-06-16 - Set to 140px to match the asset `origins` column. Both
    //   render via the shared OriginCell (maxVisible=1 + "+N" overflow counter), so
    //   a single chip + counter fits 140px identically to the assets table.
    origin: {
      width: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      label: 'Added By',
      sortable: true,
      filterable: true,
      info: 'Seed origin source',
    },

    // Confidence column - domain/ip seeds, preseed tab
    confidence: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Confidence',
      sortable: false,
      filterable: false,
      info: 'Discovery confidence score',
    },

    // Scan Profile column - seed tabs
    // Added: 2026-06-16 - "Scan Profile" label ≈ 100px + 12px th padding
    //   (pl-2+pr-1) + 24px hover sort chevron (gap-2 8px + size-4 16px) = ~136px;
    //   140px fits label + chevron with cushion. Previously seed had no `profile`
    //   entry, so it fell back to the 100px default, which clipped the label and
    //   sort icon (and emitted a "Column width not found for seed.profile" warning).
    profile: {
      width: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      label: 'Scan Profile',
      sortable: true,
      filterable: true,
      info: 'Scan profile assigned to this seed',
    },

    // Country column - IP seeds only
    // Source: SEED_COLUMN_WIDTHS_IP.country
    // Updated: 2026-06-16 - 40px→90px. At 40px the "Country" header + code clipped
    //   (IP tab).
    // Updated: 2026-06-16 - 90px→100px to match asset.country. The "Country" header
    //   (~55px) + 12px th padding + 24px hover sort chevron = ~91px; 90px left no room
    //   for the sort chevron. 100px matches the assets table with ~9px cushion.
    country: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Country',
      sortable: true,
      filterable: true,
      info: 'Country code (ISO 3166-1 alpha-2)',
    },

    // NOTE: status, tags, created, and visited are intentionally NOT overridden
    // here — seed falls back to the shared widths (status 130, tags 130,
    // created/visited 120) so these shared columns match their asset
    // counterparts. The seed "Approved" status pill is ~91px and fits the shared
    // 130px comfortably under the non-responsive CanonicalTable.
  },

  /**
   * ========================================================================
   * ASSET-SPECIFIC COLUMNS
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/asset/config/columns.ts
   *
   * Asset table columns for domains, IPs, web applications, and network services.
   * Common columns (status, tags, created, visited) are in shared section.
   */
  asset: {
    // Identifier column - Main asset identifier
    // Source: ASSET_COLUMN_WIDTHS.identifier
    // The table's flexible absorber column: minWidth only (no fixedWidth) so it
    // routes through the isExpandable/minWidth path in applyColumnWidth. Under
    // tableLayout:'fixed' in non-responsive mode its <col> becomes width:auto and
    // soaks up leftover viewport width, letting the fixed-width columns render at
    // their true spec instead of being proportionally inflated.
    identifier: {
      width: { minWidth: 200 },
      label: 'Asset',
      sortable: true,
      filterable: true,
      info: 'Asset identifier (domain, IP, URL)',
    },

    // Severity tier badge — Asset.riskSeverity (C/H/M/L/I/E). Non-sortable: not a
    // frontend-orderBy-able column (backend AllowedColumns uses snake_case risk_severity).
    // Updated: 2026-06-13 - 80px→120px. At 80px the "Asset Risk" header clipped (cell
    // padding eats into the ~74px label). 120px gives the header + RiskTag pill room.
    severity: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Asset Risk',
      sortable: false,
      filterable: false,
      info: 'Asset risk severity tier derived from risk score',
    },

    // Risk score 0-100 — Asset.riskScore (neo4j: risk_score).
    // The ⓘ info icon is omitted from this header (columnDefinitions.tsx riskScore
    // sets no `info` prop), but the column IS sortable, so the header renders a
    // click-to-sort chevron (hidden at rest, group-hover:block on hover).
    // Updated: 2026-06-13 - 90px→115px. Live DOM measurement: "Risk Score" label
    // is ~71px (600 14px Inter); on hover the revealed chevron adds gap-2 (8px) +
    // a size-4 (16px) icon, so the header span needs 95px and the th needs 107px
    // (pl-2+pr-1=12px padding). 90px clipped the hover chevron by 17px; 115px
    // fits label + gap + chevron with ~8px cushion.
    riskScore: {
      width: { fixedWidth: 115, minWidth: 115, maxWidth: 115 },
      label: 'Risk Score',
      sortable: true,
      filterable: false,
      info: 'Computed risk score (0-100) reflecting overall attack surface severity',
    },

    // Application column - the parent Application's FQDN, populated from
    // Asset.group when this observation belongs to a rollup root. Renders
    // as a chip that opens the asset drawer's Application section.
    application: {
      width: { fixedWidth: 200, minWidth: 150 },
      label: 'Application',
      sortable: false, // Asset.group isn't in the sortable backend columns
      filterable: false,
      info: 'Parent Application this observation rolls up under',
    },

    // Type column — categorical kind of asset row (Application / Web
    // Application / IP / Domain / Other). Derived via `assetType()`;
    // makes the heterogeneous /my asset bucket legible.
    type: {
      width: { fixedWidth: 130, minWidth: 100, maxWidth: 160 },
      label: 'Type',
      sortable: false, // Derived client-side
      filterable: false,
      info: 'Asset kind (Application, IP, Domain, Web Application, Other)',
    },

    // ASN Name column
    // Source: ASSET_COLUMN_WIDTHS.asname
    asname: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'ASN Name',
      sortable: true,
      filterable: true,
      info: 'Autonomous System Name',
    },

    // ASN Number column
    // Source: ASSET_COLUMN_WIDTHS.asnumber
    // Updated: 2026-06-13 - 100px→122px. The displayed "AS Number" header (label is
    // overridden in columnDefinitions.tsx) is 78px at 600 14px Inter, and the column
    // is sortable, so the header reserves a hover sort chevron (gap-2 8px + size-4
    // 16px): required = 78 + 12 (pl-2+pr-1) + 24 = 114px. 100px clipped the chevron
    // by 14px. 122px fits header + chevron (~8px cushion) and a max 10-digit ASN value.
    asnumber: {
      width: { fixedWidth: 122, minWidth: 122, maxWidth: 122 },
      label: 'ASN',
      sortable: true,
      filterable: true,
      info: 'Autonomous System Number',
    },

    // Country column
    // Source: ASSET_COLUMN_WIDTHS.country
    // Updated: 2026-06-13 - 80px→100px. The "Country" header is 55px at 600 14px
    // Inter, and the column is sortable, so the header reserves a hover sort chevron
    // (gap-2 8px + size-4 16px): required = 55 + 12 (pl-2+pr-1) + 24 = 91px. 80px
    // clipped the chevron by 11px. 100px fits header + chevron with ~9px cushion.
    country: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Country',
      sortable: true,
      filterable: true,
      info: 'Country code (ISO 3166-1 alpha-2)',
    },

    // IP Version column
    // Source: ASSET_COLUMN_WIDTHS.ipVersion
    // Updated: 2026-06-13 - 50px→90px. The "IP Version" header measured 81px
    // (600 14px Inter) and clipped at 50px. 90px clears the header against the th
    // padding (pl-2+pr-1=12px).
    ipVersion: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'IP Ver',
      sortable: false,
      filterable: false,
      info: 'IP version (IPv4 or IPv6)',
    },

    // Attack Surface column
    // Source: ASSET_COLUMN_WIDTHS.attackSurface
    // Updated: 2026-06-13 - 80px→150px. The "Attack Surface" header measured 113px
    // (600 14px Inter) and clipped at 80px; cells render an "EXTERNAL" pill plus a
    // "+N" overflow counter needing ~140px. 150px fits header + pill + counter.
    attackSurface: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'Attack Surface',
      sortable: true,
      filterable: true,
      info: 'Attack surface classification',
    },

    // Scan Profile column
    // Updated: 2026-06-16 - 120px→140px. "Scan Profile" label ≈ 100px + 12px th
    //   padding (pl-2+pr-1) + 24px hover sort chevron (gap-2 8px + size-4 16px) =
    //   ~136px; 120px clipped the hover sort chevron. 140px fits label + chevron
    //   with cushion and matches the new seed.profile (identical sortable header).
    profile: {
      width: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      label: 'Scan Profile',
      sortable: true,
      filterable: true,
      info: 'Scan profile assigned to this asset',
    },

    // Origins column
    // Source: ASSET_COLUMN_WIDTHS.origins
    // Updated: 2026-06-13 - 100px→140px. A single "Praetorian" chip fills 100px
    // exactly; multi-origin cells add a "+N" overflow counter that clipped at 100px
    // (verified in screenshot). 140px fits the chip + counter.
    origins: {
      width: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      label: 'Origins',
      sortable: true,
      filterable: true,
      info: 'Discovery sources for this asset',
    },

    // Port column
    // Source: ASSET_COLUMN_WIDTHS.port
    port: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Port',
      sortable: true,
      filterable: true,
      info: 'Network port number',
    },

    // Protocol column
    // Source: ASSET_COLUMN_WIDTHS.protocol
    protocol: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Proto',
      sortable: true,
      filterable: true,
      info: 'Network protocol',
    },
  },

  /**
   * ========================================================================
   * TECHNOLOGY-SPECIFIC COLUMNS
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/technology/config/columns.ts
   *
   * Technology table columns for displaying detected technologies.
   * Common columns (tags, created, visited) are in shared section.
   */
  technology: {
    // Vendor column
    // Source: TECHNOLOGY_COLUMN_WIDTHS.vendor
    // Updated: 2025-11-18 - Increased from 60px to 70px for better vendor name display
    // Updated: 2026-01-08 - Increased from 100px to 120px to fit full vendor names (e.g., "paloaltonetworks")
    vendor: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Vendor',
      sortable: true,
      filterable: true,
      info: 'Technology vendor/manufacturer',
    },

    // Name column - Main technology name
    // Source: TECHNOLOGY_COLUMN_WIDTHS.name
    name: {
      width: { minWidth: 300 },
      label: 'Technology',
      sortable: true,
      filterable: true,
      info: 'Technology product name',
    },

    // Product column - Technology product name
    // Source: TechnologiesTab.tsx (130px)
    product: {
      width: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      label: 'Product',
      sortable: true,
      filterable: true,
      info: 'Technology product name',
    },

    // Version column - Technology version number
    // Source: TechnologiesTab.tsx (100px)
    version: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Version',
      sortable: true,
      filterable: true,
      info: 'Technology version number',
    },

    // CPE String column - Common Platform Enumeration
    // Source: TechnologiesTab.tsx (flexible)
    cpeString: {
      width: { minWidth: 250 }, // Flexible for long CPE strings
      label: 'CPE String',
      sortable: false,
      filterable: false,
      info: 'Common Platform Enumeration identifier',
    },
  },

  /**
   * ========================================================================
   * RISK-SPECIFIC COLUMNS (VULNERABILITIES)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/vulnerabilities/config/columns.ts
   *
   * Vulnerability/Risk table columns for security risk management.
   * Note: Backend uses "Risk" entity type, but UI refers to these as "Vulnerabilities"
   *
   * Common columns (created, visited) are in shared section.
   *
   * Migration: Day 5 - 2025-10-23
   * - Migrated from COLUMN_WIDTHS in vulnerabilities/config/columns.ts
   * - Standardized date columns (created/visited) to match Assets/Technologies (60px)
   * - Previous values: created=180px, visited=180px
   */
  risk: {
    // Status column - Vulnerability lifecycle state
    // Source: COLUMN_WIDTHS.status (150px)
    // Updated: 2025-11-18 - Reduced from 110px to 100px for drawer space optimization
    // Updated: 2025-12-29 - Added compact variant for drawer context (+35px for "DEMONSTRATED" text)
    // Updated: 2026-03-06 - Increased default from 95px to 120px to prevent "DEMONSTRATED" truncation (ENG-1919)
    // Updated: 2026-06-17 - 120→140 default, 130→140 compact. 120/130 still clipped "DEMONSTRATED"
    //   (the longest pentesting-mode label) at the table's BASE width — i.e. on a narrow
    //   viewport, where tableLayout:'fixed' has no extra space to distribute and the column
    //   sits at exactly its fixedWidth. The StatusChip needs ~120px (px-2 16 + border 2 +
    //   icon size-3 12 + gap-1.5 6 + "DEMONSTRATED" Inter-600 10px uppercase ~84) and the td
    //   adds pl-2+pr-1 = 12px → ~132px hard-fit. 140 = requirement + ~8px cushion. Locked by
    //   the "DEMONSTRATED fit" invariant in registry.test.ts.
    status: {
      width: {
        default: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
        compact: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      },
      label: 'Status',
      sortable: true,
      info: 'Vulnerability status (Opened, Triaged, Remediated, Accepted, Deleted)',
    },

    // Priority/Severity column
    // Source: COLUMN_WIDTHS.priority (120px)
    // Updated: 2025-10-27 - Increased from 40px to 100px for severity badges
    // Updated: 2025-12-29 - Added compact variant for drawer context (+10px for better badge display)
    // Updated: 2026-06-13 - 80px→100px (both variants). Sortable header "Severity" (label 56px)
    //   needs 56+12+24=92px for the hover sort chevron (12px th padding pl-2+pr-1, plus gap-2
    //   8px + size-4 16px chevron); 80px clipped it. 100px = req + 8px cushion.
    priority: {
      width: {
        default: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
        compact: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      },
      label: 'Severity',
      sortable: false,
      info: 'Vulnerability severity level (Critical, High, Medium, Low, Info, Exposure)',
    },

    // Severity column - Alias for priority (used in query builder filter names)
    // VulnerabilityEntityConfig maps 'severity' → 'priority' field
    severity: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Severity',
      sortable: false,
      info: 'Vulnerability severity level (Critical, High, Medium, Low, Info, Exposure)',
    },

    // Name column - Main vulnerability identifier
    // Source: COLUMN_WIDTHS.name (300px)
    // Updated: 2025-12-29 - Added compact variant for drawer context (flexible width 250-350px)
    name: {
      width: {
        default: { fixedWidth: 400, minWidth: 400, maxWidth: 400 },
        compact: { fixedWidth: undefined, minWidth: 250, maxWidth: 350 }, // Flexible in drawer - explicitly unset fixedWidth
      },
      label: 'Vulnerability',
      sortable: true,
      filterable: false,
      info: 'Vulnerability name/title',
    },

    // CVSS Score column
    // Source: COLUMN_WIDTHS.cvss (100px)
    cvss: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'CVSS',
      sortable: false,
      info: 'Common Vulnerability Scoring System score',
    },

    // EPSS Score column
    // Source: COLUMN_WIDTHS.epss (100px)
    epss: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'EPSS',
      sortable: false,
      info: 'Exploit Prediction Scoring System probability',
    },

    // KEV (Known Exploited Vulnerability) column
    // Source: COLUMN_WIDTHS.kev (100px)
    // Updated: 2026-06-13 - 100px→110px. Sortable header "CISA KEV" (label 65px) needs
    //   65+12+24=101px for the hover sort chevron (12px th padding pl-2+pr-1, plus gap-2
    //   8px + size-4 16px chevron); 100px clipped it. 110px = req + 9px cushion.
    kev: {
      width: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      label: 'CISA KEV',
      sortable: false,
      info: 'CISA Known Exploited Vulnerabilities catalog status',
    },

    // ITSM Ticket column
    // Added: 2026-02-23 - ITSM ticket number for vulnerability tracking (ENG-390)
    ticket: {
      width: { fixedWidth: 160, minWidth: 160, maxWidth: 160 },
      label: 'Ticket',
      sortable: false,
      filterable: false,
      info: 'ITSM ticket linked to this vulnerability',
    },

    frameworks: {
      width: {
        default: { fixedWidth: 180, minWidth: 180, maxWidth: 180 },
        compact: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      },
      label: 'Frameworks',
      sortable: false,
      filterable: false,
      info: 'Security frameworks mapped to this vulnerability (OWASP, CIS, etc.)',
    },

    // Origin column
    // Source: COLUMN_WIDTHS.origin (140px)
    // Updated: 2025-10-27 - Increased from 50px to 100px for origin badges
    // Updated: 2025-12-29 - Added compact variant for drawer context (hidden via 0px width)
    origin: {
      width: {
        default: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
        compact: { fixedWidth: 0, minWidth: 0, maxWidth: 0 }, // Hide in drawer
      },
      label: 'Origin',
      sortable: false,
      filterable: true,
      info: 'Vulnerability detection source (scanner, integration, manual)',
    },

    // Attack Surface column
    // Added: ENG-4330 - mirrors asset.attackSurface (150px fits header + pill + "+N")
    // sortable: false - no server-side sort for risk attack surface
    attackSurface: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'Attack Surface',
      sortable: false,
      filterable: true,
      info: 'Attack surface classification',
    },

    // Last Seen column - Override shared.visited with risk-specific variant widths
    // Source: shared.visited (80px default)
    // Updated: 2025-12-29 - Added compact variant for drawer context (130px for dates like "15 minutes ago")
    // Updated: 2026-06-13 - default 90px→110px (compact 130px unchanged). Sortable header
    //   "Last Seen" (label 67px) needs 67+12+24=103px for the hover sort chevron (12px th
    //   padding pl-2+pr-1, plus gap-2 8px + size-4 16px chevron); 90px clipped it. 110px =
    //   req + 7px cushion. Compact (130px) already had room.
    // Updated: 2026-06-19 - default 110px→120px to match shared.visited (120px), so the
    //   "Last Seen" column renders at the same width across Seeds/Preseeds/Assets/
    //   Technologies/Vulnerabilities. 120px still clears the 103px sort-chevron requirement
    //   (now +17px cushion). Compact (130px, drawer-only) unchanged.
    visited: {
      width: {
        default: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
        compact: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      },
      label: 'Last Seen',
      sortable: true,
      info: 'Date vulnerability was last observed',
    },

    // Demonstrated column - Date the risk most recently moved into the Open
    // ("Demonstrated") state. Risk-only (no shared fallback) since only risks carry
    // a demonstration timestamp. Sortable, server-side via risk.demonstrated.
    // Width 140px: the sortable header "Demonstrated" (~92px) plus the 12px th
    // padding (pl-2+pr-1) and the 24px hover sort chevron (gap-2 8px + size-4 16px)
    // need ~128px; 140px leaves ~12px cushion. Mirrors the status column's band.
    demonstrated: {
      width: {
        default: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
        compact: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      },
      label: 'Demonstrated',
      sortable: true,
      info: 'Date vulnerability was moved to Demonstrated',
    },

    // CVE column - CVE identifier for the vulnerability
    // Added: 2025-12-05 - Added to support CVE filter in query builder
    cve: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'CVE',
      sortable: true,
      filterable: true,
      info: 'Common Vulnerabilities and Exposures identifier (e.g., CVE-2025-55182)',
    },

    // GUID column - Unique vulnerability instance identifier (UUID v4)
    guid: {
      width: {
        default: { fixedWidth: 85, minWidth: 85, maxWidth: 85 },
      },
      label: 'ID',
      sortable: false,
      info: 'Unique identifier for this vulnerability instance (UUID v4)',
    },

    // Note: created and visited columns use shared date columns
    // Previous local values: created=180px, visited=180px
    // New standardized values: 60px (matches Assets/Technologies)
  },

  /**
   * ========================================================================
   * AGENT-SPECIFIC COLUMNS (AEGIS AGENTS)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/agents/config/columnDefinitions.tsx
   *
   * Aegis security agent table columns for agent management and monitoring.
   * Agents are sourced from Velociraptor backend (not Neo4j/DynamoDB).
   *
   * Common columns (last_seen_at uses shared 'visited' pattern) stored in shared section.
   *
   * Migration: Day 6 - 2025-10-23
   * - Migrated from local columnDefinitions.tsx to centralized registry
   * - Maintains agent-specific data sources while centralizing cosmetics
   * - Previous values: status=150px, client_id=250px, last_seen_at=60px (CELL_WIDTHS.date)
   */
  agent: {
    // Status column - Agent online/offline/warning/error state
    // Source: columnDefinitions.tsx status (150px)
    // Note: Uses AgentStatusBadge component for rendering
    // Updated: 2025-12-06 - Standardized to 70px to match shared status column
    status: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: 'Status',
      sortable: true,
      info: 'Endpoint connection status (Online, Offline, Warning, Error)',
    },

    // Client ID column - Unique agent identifier
    // Source: columnDefinitions.tsx client_id (250px)
    // Note: Shows Cloudflare tunnel icon when configured
    client_id: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Endpoint',
      sortable: true,
      info: 'Unique endpoint identifier (Client ID)',
    },

    // FQDN/Asset column - Fully qualified domain name or hostname
    // Source: columnDefinitions.tsx fqdn (expandable, no fixed width)
    // Note: Displays FQDN with IP address dropdown
    fqdn: {
      width: { minWidth: 160 },
      label: 'Asset',
      sortable: true,
      info: 'Endpoint fully qualified domain name (FQDN) or hostname',
    },

    // Last Seen column - Last agent check-in timestamp
    // Source: CELL_WIDTHS.date (60px) - matches Assets/Technologies/Risks
    // Note: Uses shared 'visited' pattern for consistency
    // Updated: 2025-12-06 - Standardized to 80px to match shared date columns (created/visited)
    last_seen_at: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Last Seen',
      sortable: true,
      info: 'Last time endpoint checked in with server',
    },
  },

  /**
   * ========================================================================
   * AGENT USER COLUMNS (AEGIS AGENT USER MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/agents/components/aegis-management/user/UserManagementTab.tsx
   *
   * Linux user management columns for Aegis agents.
   * Used in AegisManagementModal Users tab.
   *
   * Migration: 2025-12-06
   * - Migrated from inline column definitions to centralized registry
   * - Simplified table from Table to TanStackTable component
   */
  agentUser: {
    // Username column - Linux system username
    // Updated: 2025-12-07 - Changed from expandable to fixed to prevent huge white space
    username: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'User',
      sortable: true,
      info: 'Linux system username (first.last format)',
    },

    // Status column - User account status
    // Updated: 2025-12-07 - Reduced from 100px to 90px (sufficient for ACTIVE badge)
    status: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Status',
      sortable: false,
      info: 'User account status (always Active)',
    },

    // Actions column - Delete action
    // Updated: 2025-12-07 - Increased from 60px to 70px for better right padding
    actions: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: '',
      sortable: false,
    },
  },

  /**
   * ========================================================================
   * AGENT CREDENTIAL COLUMNS (AEGIS AGENT CREDENTIALS MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/agents/components/aegis-management/credentials/CredentialsManagementTab.tsx
   *
   * Active Directory credential management columns for Aegis agents.
   * Used in AegisManagementModal Credentials tab.
   *
   * Migration: 2025-12-07
   * - Migrated from inline list view to TanStackTable pattern
   * - Follows UserManagementTab pattern for consistency
   */
  agentCredential: {
    // Name column - Credential label
    name: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Credential',
      sortable: false,
      info: 'Credential label or name',
    },

    // Credential ID column - Unique identifier
    credentialId: {
      width: { fixedWidth: 280, minWidth: 280, maxWidth: 280 },
      label: 'ID',
      sortable: false,
      info: 'Unique credential identifier',
    },

    // Created column - When credential was added
    created: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Created',
      sortable: true,
      info: 'When credential was added',
    },

    // Actions column - Delete action
    actions: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: '',
      sortable: false,
    },
  },

  /**
   * ========================================================================
   * CUSTOMER-SPECIFIC COLUMNS (CUSTOMER MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/customerManagement/config/columns.ts
   *
   * Customer management table columns for Praetorian staff to manage all customer tenants.
   * Displays subscription status, customer type, risk metrics, and operational health.
   *
   * Migration: Day 7 - 2025-10-24
   * - Migrated from CUSTOMER_COLUMN_WIDTHS in customerManagement/config/columns.ts
   * - Note: 'high' column had inconsistent widths (fixedWidth: 60, minWidth: 80, maxWidth: 80)
   *   Using fixedWidth: 60 as source of truth (likely typo in original config)
   */
  customer: {
    // View column - Action button to view customer account
    view: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 50 },
      label: 'View',
      sortable: false,
      info: 'Open customer account details',
    },

    // Status column - Customer lifecycle status
    // Updated: 2025-10-27 - Increased from 40px to 110px for status badges
    // Updated: 2026-06-17 - value corrected 60→110 (the 2025-10-27 edit set 60, not the
    //   110 its comment claimed), so "COMPLETED" (longest customer status, icon-less
    //   StatusChip) no longer clips. Hard-fit: chip px-2 16 + border 2 + "COMPLETED"
    //   Inter-600 10px uppercase ~63 = ~81px; cell pl-2+pr-1 = 12px → ~93px. 110 = +~17px
    //   cushion. Locked by the "COMPLETED fit" invariant in registry.test.ts.
    status: {
      width: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      label: 'Status',
      sortable: true,
      info: 'Customer lifecycle status (Active, Paused, Setup, Upcoming, Completed)',
    },

    // Display Name column - Primary customer identifier (expandable)
    displayName: {
      width: { fixedWidth: 300, minWidth: 200 },
      label: 'Customer',
      sortable: true,
      info: 'Customer display name with logo',
    },

    // Customer Type column
    type: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 130 },
      label: 'Type',
      sortable: true,
      info: 'Customer type classification',
    },

    // EVM Score column - Combined delivery value + platform adoption
    evm: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'EVM',
      sortable: true,
      info: 'EVM score (0-10): 60% DVS + 40% PAS',
    },

    // DVS column - Delivery Value Score
    dvs: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'DVS',
      sortable: true,
      info: 'Delivery Value Score (0-10): severity-weighted findings with decay',
    },

    // PAS column - Platform Adoption Score
    pas: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'PAS',
      sortable: true,
      info: 'Platform Adoption Score (0-10): integrations, logins, coverage, capabilities',
    },

    // Integration Health column
    health: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Health',
      sortable: true,
      info: 'Integration health (healthy / total)',
    },

    // Last Compromise Date column
    // Updated: 2026-06-18 - 90px→165px. At 90px both the "Last Compromise" header
    //   and the date-pill values clipped. The header is the binding constraint:
    //   "Last Compromise" (Inter-600 14px ≈ 120px) + 12px th padding (pl-2+pr-1) +
    //   24px hover sort chevron (gap-2 8px + size-4 16px) = ~156px; the column is
    //   sortable so the chevron space is always reserved. 165px = header + chevron
    //   with ~9px cushion, and comfortably fits the "MMM d, yyyy" date pill
    //   (e.g. "Jun 18, 2026") / "Never" chip which need only ~108px.
    lastCompromiseDate: {
      width: { fixedWidth: 165, minWidth: 165, maxWidth: 165 },
      label: 'Last Compromise',
      sortable: true,
      info: 'Date of most recent critical demonstrated risk',
    },

    // Deletion column - Tenant-deletion lifecycle state (Praetorian-only)
    // Renders an icon-less StatusChip whose longest label is "REQUESTED"/
    // "COMPLETED"/"CANCELLED" (9 chars uppercased), matching the customer
    // `status` column's proven "COMPLETED fit" — so 110px carries it without
    // clipping. Empty when the tenant has no deletion record.
    deletion: {
      width: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      label: 'Deletion',
      sortable: false,
      info: 'Tenant deletion state (Requested, Scheduled, Purging, Completed, Failed, Cancelled)',
    },

    // Critical Risk Count column
    critical: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Critical',
      sortable: true,
      info: 'Critical severity risk count',
    },

    // High Risk Count column (FIXED: was fixedWidth: 60, minWidth: 80, maxWidth: 80)
    high: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'High',
      sortable: true,
      info: 'High severity risk count',
    },

    // Medium Risk Count column
    medium: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Medium',
      sortable: true,
      info: 'Medium severity risk count',
    },

    // Low Risk Count column
    low: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Low',
      sortable: true,
      info: 'Low severity risk count',
    },

    // Info Risk Count column
    info: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Info',
      sortable: true,
      info: 'Info severity risk count',
    },
  },

  /**
   * ========================================================================
   * JOB-SPECIFIC COLUMNS (ASYNC JOB EXECUTION)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/jobs/config/columns.ts
   *
   * Job execution tracking columns for security scan job management.
   * Jobs represent async security tool executions with real-time status tracking.
   *
   * Note: Status column uses platform-standard width (110px) for consistency
   * with other entity status badges. Updated column uses shared.visited pattern
   * (130px) for standardized date formatting.
   *
   * Migration: 2025-11-15
   * - Migrated from COLUMN_WIDTHS in jobs/config/columns.ts
   * - Standardized status column from 100px → 110px (platform standard)
   * - Standardized updated column from flexible → 130px (shared.visited pattern)
   * - Added proper column IDs for sourceTarget and actions
   * - Enabled sorting for updated column
   * - Previous values: status=100px, sourceTarget=flexible, updated=flexible, actions=120px
   */
  job: {
    // Status column - Job execution state
    // Source: jobs/config/columns.ts status (100px)
    // Updated: Increased from 40px → 60px for better header text display
    status: {
      width: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
      label: 'Status',
      sortable: true, // Job status changes in real-time via polling
      info: 'Job execution status (Running, Queued, Pass, Fail)',
    },

    // Job Type column - Displays the type of security scan being executed
    // Migration: 2025-11-15 - Split from sourceTarget column
    // Updated: 2025-12-07 - Increased from 130px → 200px for long job names
    // Examples: "LINUX-AD-CERTIFY#2025-12-05T16:18:58Z" (43 chars)
    type: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Job',
      sortable: true,
      info: 'Type of security scan job (port-scanning, nuclei, ad-certify, etc.)',
    },

    // Source column - Displays the source entity being scanned
    // Migration: 2025-11-15 - Split from sourceTarget column
    // Fixed width for stable layout
    source: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Source',
      sortable: false,
      info: 'Source asset or entity being scanned',
    },

    // Target column - Displays the target DNS or IP being scanned
    // Migration: 2025-11-15 - Split from sourceTarget column
    // Expandable to accommodate long DNS names
    target: {
      width: { minWidth: 400 },
      label: 'Target',
      sortable: false,
      info: 'Target DNS or IP address being scanned',
    },

    // Created column - Job creation timestamp
    // Added: 2025-11-16 - Following platform pattern (Asset, Vulnerability, Seed all show created + updated)
    // Uses shared date column width (130px) for consistency with other tables
    created: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Job Created Time',
      sortable: true,
      info: 'Timestamp when job was created and queued in the system',
    },

    // Updated column - Last status change timestamp
    // Source: jobs/config/columns.ts updated (flexible)
    // Updated: Changed from flexible → 130px fixed (matches shared.visited pattern)
    // Note: For completed jobs this represents completion time, for running jobs when they started
    // Enable sorting to allow users to see most recent jobs first
    updated: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Job Completion Time',
      sortable: true, // Enable sorting by recency
      info: 'Last status change timestamp (completion time for finished jobs)',
    },
  },

  /**
   * ========================================================================
   * ACCOUNT-SPECIFIC COLUMNS (USER MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/components/AuthorizedUsersTables.tsx
   *
   * User management table columns for displaying authorized users.
   * Used in settings and customer management interfaces.
   *
   * Variant Support (2025-12-02):
   * - default: Full page context (Users page) - 900px+ available width
   * - compact: Drawer context (Customer Management) - ~900px constrained width
   *
   * Compact Column Layout (Role hidden):
   * - displayName: 140px (truncates with OverflowText)
   * - authMethod: 80px (shortened label "Auth Method")
   * - created: 110px (date format fits comfortably)
   * - lastActivity: 110px (date format fits comfortably)
   * - actions: 60px (two icon buttons)
   * - Total: 500px (400px buffer for drawer padding/margins)
   *
   * Migration: 2025-12-02
   */
  account: {
    // Display Name column - User identifier (email or display name)
    displayName: {
      width: {
        default: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
        compact: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      },
      label: 'User',
      sortable: true,
      info: 'User display name or email address',
    },

    // Role column - User permission level
    // Note: Hidden in compact variant via column visibility logic in AuthorizedUsersTables.tsx
    role: {
      width: {
        default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
        compact: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      },
      label: 'Role',
      sortable: true,
      info: 'User role (Account owner, Collaborator)',
    },

    // Authentication Method column - SSO vs local auth
    authMethod: {
      width: {
        default: { fixedWidth: 250, minWidth: 250, maxWidth: 250 },
        compact: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      },
      label: 'Authentication Method',
      sortable: false,
      info: 'Single Sign-On or local authentication',
    },

    // Created column - Account creation timestamp
    created: {
      width: {
        default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
        compact: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      },
      label: 'Account Creation',
      sortable: true,
      info: 'Date user account was created',
    },

    // Last Activity column - Last access timestamp
    lastActivity: {
      width: {
        default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
        compact: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      },
      label: 'Last Activity',
      sortable: true,
      info: 'Last time user accessed the account',
    },

    // Actions column - Management actions
    actions: {
      width: {
        default: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
        compact: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
      },
      label: 'Actions',
      sortable: false,
      info: 'User management actions (revoke, reinvite)',
    },
  },

  /**
   * ========================================================================
   * ACCESS-SPECIFIC COLUMNS (ACCESS LOGS)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/UsersPage.tsx
   *
   * Access log table columns for displaying user access history.
   * Used in Users page to track SSO and username/password logins.
   *
   * Migration: 2025-12-02
   */
  access: {
    // Email column - User identifier (expandable to fill remaining table width)
    // Updated: 2025-12-10 - Increased to 350px to fit full email addresses
    // Updated: 2026-03-13 - Changed to expandable (removed maxWidth) to fill container width
    email: {
      width: { minWidth: 200 },
      label: 'User',
      sortable: true,
      info: 'User email address',
    },

    // Access Type column - SSO or Username/Password
    // Updated: 2025-12-10 - Increased to 200px to help fill table width
    type: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Access Type',
      sortable: true,
      info: 'Authentication method (SSO or Username/Password)',
    },

    // Role column - User's RBAC role
    role: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'Role',
      sortable: true,
      info: 'User role in the organization',
    },

    // SSO Domain column - Domain for SSO users
    // Updated: 2026-03-13 - Changed to expandable (no maxWidth) to accommodate long SSO domains
    value: {
      width: { minWidth: 180 },
      label: 'SSO Domain',
      sortable: false,
      info: 'SSO domain (only for SSO users)',
    },

    // Note: lastActivity uses shared.visited pattern
    // Updated timestamp - Last access time (uses shared 'visited' column)
  },

  /**
   * ========================================================================
   * CAPABILITY-SPECIFIC COLUMNS (SECURITY SCANNING CAPABILITIES)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/attacks/capabilities/Capabilities.tsx
   *
   * Capability table columns for displaying security scanning capabilities.
   * Capabilities represent scanning modules that can be executed against assets.
   *
   * Migration: 2025-12-15
   * - Migrated from inline column definitions to centralized registry
   * - Follows established column registry patterns
   */
  capability: {
    // Title/Name column - Capability display name
    title: {
      width: { fixedWidth: 180, minWidth: 180, maxWidth: 180 },
      label: 'Name',
      sortable: true,
      info: 'Capability name and version',
    },

    // Description column - Capability description (expandable)
    description: {
      width: { minWidth: 200 }, // Expandable column, no maxWidth
      label: 'Description',
      sortable: false,
      info: 'Capability description and purpose',
    },

    // Surface column - Execution surface (external, internal, cloud, scm)
    surface: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Surface',
      sortable: true,
      filterable: true,
      info: 'Execution surface (External, Internal, Cloud, SCM)',
    },

    // Target column - Resource type the capability operates on
    target: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Resource',
      sortable: true,
      filterable: true,
      info: 'Target resource type (Domain, IP, WebApp, AD Domain)',
    },

    // Category column - Capability category classification
    category: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Category',
      sortable: true,
      filterable: true,
      info: 'Capability category (Discovery, Enumeration, Exploitation)',
    },

    // Platform column - Platform requirement for capability execution
    runs_on: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Platform',
      sortable: true,
      filterable: true,
      info: 'Platform requirement (Linux, Windows, macOS)',
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Helper function to get column width configuration with variant support
 *
 * Lookup strategy:
 * 1. Check entity-specific columns first (shared, seed, asset, technology, risk, etc.)
 * 2. If not found in entity-specific, fall back to shared columns (for backward compatibility)
 * 3. Return default width if not found (with console warning)
 *
 * Variant handling:
 * - If column has variant-aware widths, returns the appropriate variant
 * - Compact variant merges overrides with default base values
 * - Legacy columns (no variants) return their width directly
 *
 * @param entityType - Entity type (shared, seed, asset, technology, risk, agent, customer, job, account, access)
 * @param columnKey - Column key (email, status, identifier, port, protocol, etc.)
 * @param variant - Table variant ('default' or 'compact'), defaults to 'default'
 * @returns Column width configuration for the specified variant
 *
 * @example
 * ```typescript
 * // Get default variant width
 * const width = getColumnWidth('account', 'displayName');
 * // { fixedWidth: 150, minWidth: 150, maxWidth: 150 }
 *
 * // Get compact variant width
 * const compactWidth = getColumnWidth('account', 'displayName', 'compact');
 * // { fixedWidth: 120, minWidth: 120, maxWidth: 120 }
 *
 * // Legacy column (no variants) - returns same width regardless of variant
 * const legacyWidth = getColumnWidth('seed', 'email', 'compact');
 * // { fixedWidth: 200, minWidth: 200, maxWidth: 200 }
 * ```
 */
export function getColumnWidth(
  entityType: EntityType,
  columnKey: string,
  variant: TableVariant = 'default'
): ColumnWidth {
  // Try entity-specific first
  const entityConfig = COLUMN_REGISTRY[entityType]?.[columnKey];
  const sharedConfig = COLUMN_REGISTRY.shared[columnKey];

  const config = entityConfig || sharedConfig;

  if (!config) {
    // Default width if not found
    console.warn(
      `Column width not found for ${entityType}.${columnKey}, using default`
    );
    return { fixedWidth: 100, minWidth: 100, maxWidth: 100 };
  }

  const widthConfig = config.width;

  // Check if width has variant support
  if (hasVariants(widthConfig)) {
    if (variant === 'compact' && widthConfig.compact) {
      // Merge compact overrides with default base
      const merged: ColumnWidth = {
        ...widthConfig.default,
        ...widthConfig.compact,
      };

      // Validate fixed column invariant after merge
      if (merged.fixedWidth !== undefined) {
        if (
          merged.fixedWidth !== merged.minWidth ||
          merged.fixedWidth !== merged.maxWidth
        ) {
          console.warn(
            `[Table Registry] Invalid variant config for ${entityType}.${columnKey}: ` +
              `fixedWidth (${merged.fixedWidth}) must equal minWidth (${merged.minWidth}) and maxWidth (${merged.maxWidth}). ` +
              `Auto-correcting to use fixedWidth value.`
          );
          return {
            fixedWidth: merged.fixedWidth,
            minWidth: merged.fixedWidth,
            maxWidth: merged.fixedWidth,
          };
        }
      }

      return merged;
    }
    // Return default variant
    return widthConfig.default;
  }

  // Legacy: width is a plain ColumnWidth object (no variants)
  return widthConfig;
}

/**
 * Helper function to get full column configuration
 *
 * Returns complete ColumnConfig including width, label, sortable, etc.
 *
 * @param entityType - Entity type
 * @param columnKey - Column key
 * @returns Full column configuration or default
 *
 * @example
 * ```typescript
 * const emailConfig = getColumnConfig('seed', 'email');
 * // {
 * //   width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
 * //   label: 'Email',
 * //   sortable: true,
 * //   filterable: true,
 * //   info: 'Email address'
 * // }
 * ```
 */
export function getColumnConfig(
  entityType: EntityType,
  columnKey: string
): ColumnConfig {
  const entityConfig = COLUMN_REGISTRY[entityType]?.[columnKey];
  if (entityConfig) {
    return entityConfig;
  }

  const sharedConfig = COLUMN_REGISTRY.shared[columnKey];
  if (sharedConfig) {
    return sharedConfig;
  }

  return {
    width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
  };
}

/**
 * Get all column configurations for an entity type
 *
 * Merges shared columns with entity-specific columns.
 * Entity-specific columns override shared if there are naming conflicts.
 *
 * @param entityType - Entity type
 * @returns All column configurations including shared
 *
 * @example
 * ```typescript
 * const seedColumns = getEntityColumns('seed');
 * // Returns: { email, status, created, visited, identifier, registrar, registrant, origin, country }
 * ```
 */
export function getEntityColumns(entityType: EntityType): EntityColumns {
  return {
    ...COLUMN_REGISTRY.shared,
    ...COLUMN_REGISTRY[entityType],
  };
}

/**
 * Check if a column is sortable
 *
 * @param entityType - Entity type
 * @param columnKey - Column key
 * @returns Boolean indicating sortability
 *
 * @example
 * ```typescript
 * isColumnSortable('seed', 'email'); // true
 * isColumnSortable('asset', 'ipVersion'); // false
 * ```
 */
export function isColumnSortable(
  entityType: EntityType,
  columnKey: string
): boolean {
  const config = getColumnConfig(entityType, columnKey);
  return config?.sortable ?? false;
}

/**
 * Check if a column is filterable
 *
 * @param entityType - Entity type
 * @param columnKey - Column key
 * @returns Boolean indicating filterability
 *
 * @example
 * ```typescript
 * isColumnFilterable('seed', 'email'); // true
 * isColumnFilterable('asset', 'ipVersion'); // false
 * ```
 */
export function isColumnFilterable(
  entityType: EntityType,
  columnKey: string
): boolean {
  const config = getColumnConfig(entityType, columnKey);
  return config?.filterable ?? false;
}
